import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 10,
  total_items: 0,
  total_pages: 0
};

const defaultQuery = {
  page: 1,
  pageSize: 10,
  timeframe: 'today',
  startDate: null,
  endDate: null,
  partnerId: 'ALL',
  sortBy: 'date',
  sortDirection: 'desc'
};

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  query: defaultQuery,
  partners: [],
  partnersLoading: false,
  partnersError: null,
  exportStatus: 'idle',
  exportError: null
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };
  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }
  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const normalizeQuery = (params = {}) => ({
  page: params.page ?? defaultQuery.page,
  pageSize: params.pageSize ?? defaultQuery.pageSize,
  timeframe: params.timeframe ?? defaultQuery.timeframe,
  startDate: params.startDate ?? defaultQuery.startDate,
  endDate: params.endDate ?? defaultQuery.endDate,
  partnerId: params.partnerId ?? defaultQuery.partnerId,
  sortBy: params.sortBy ?? defaultQuery.sortBy,
  sortDirection: params.sortDirection ?? defaultQuery.sortDirection
});

const buildQueryString = (params = {}) => {
  const normalized = normalizeQuery(params);
  const search = new URLSearchParams();

  if (normalized.page != null) search.set('page', normalized.page);
  if (normalized.pageSize != null) search.set('page_size', normalized.pageSize);
  if (normalized.timeframe) search.set('timeframe', normalized.timeframe);
  if (normalized.startDate) search.set('start_date', normalized.startDate);
  if (normalized.endDate) search.set('end_date', normalized.endDate);
  if (normalized.partnerId && normalized.partnerId !== 'ALL') {
    search.set('partner_id', normalized.partnerId);
  }
  if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
  if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
};

export const fetchTransactionPartners = createAsyncThunk(
  'transactionReports/fetchPartners',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/app-sources?all=true`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data) ? data.data : [];

      return items
        .map((item) => ({
          id: item.id,
          name: item.name
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load partners.');
    }
  }
);

export const fetchTransactionReports = createAsyncThunk(
  'transactionReports/fetchReports',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const response = await fetch(`${API_BASE_URL}/api/reports/transactions${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data)
        ? data.data.map((row) => ({
            id: row.id,
            date: row.date,
            hour: row.hour,
            partnerId: row.partner?.id ?? row.partner_id ?? null,
            partnerName: row.partner?.name ?? row.partner_name ?? row.partner ?? 'Unknown',
            total: Number(row.total ?? 0),
            success: Number(row.success ?? 0),
            fail: Number(row.fail ?? 0),
            usage: Number(
              row.quotaUsage ?? row.quota_usage ?? row.usage ?? row.success ?? 0
            ),
            createdAt: row.created_at ?? row.createdAt ?? null
          }))
        : [];

      const meta = data?.meta
        ? {
            page: Number(data.meta.page ?? defaultMeta.page),
            page_size: Number(data.meta.page_size ?? defaultMeta.page_size),
            total_items: Number(data.meta.total_items ?? defaultMeta.total_items),
            total_pages: Number(data.meta.total_pages ?? defaultMeta.total_pages)
          }
        : defaultMeta;

      return {
        items,
        meta,
        query: normalizeQuery(params)
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load transaction reports.');
    }
  }
);

export const exportTransactionReports = createAsyncThunk(
  'transactionReports/exportReports',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const search = new URLSearchParams();
      const normalized = normalizeQuery(params);

      if (normalized.timeframe) search.set('timeframe', normalized.timeframe);
      if (normalized.startDate) search.set('start_date', normalized.startDate);
      if (normalized.endDate) search.set('end_date', normalized.endDate);
      if (normalized.partnerId && normalized.partnerId !== 'ALL') {
        search.set('partner_id', normalized.partnerId);
      }
      if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
      if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);

      search.set('all', 'true');

      const queryString = search.toString();
      const response = await fetch(
        `${API_BASE_URL}/api/reports/transactions/export${queryString ? `?${queryString}` : ''}`,
        {
          method: 'GET',
          headers: buildAuthHeaders(getState, {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
          })
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const message = await response.text();
        throw new Error(message || response.statusText || 'Failed to export reports.');
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename =
          match?.[1] || `transaction-reports-${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export reports.');
    }
  }
);

const transactionReportsSlice = createSlice({
  name: 'transactionReports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactionPartners.pending, (state) => {
        state.partnersLoading = true;
        state.partnersError = null;
      })
      .addCase(fetchTransactionPartners.fulfilled, (state, action) => {
        state.partnersLoading = false;
        state.partnersError = null;
        state.partners = action.payload;
      })
      .addCase(fetchTransactionPartners.rejected, (state, action) => {
        state.partnersLoading = false;
        state.partnersError = action.payload || 'Failed to load partners.';
      })
      .addCase(fetchTransactionReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionReports.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
      })
      .addCase(fetchTransactionReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load transaction reports.';
      })
      .addCase(exportTransactionReports.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportTransactionReports.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
        state.exportError = null;
      })
      .addCase(exportTransactionReports.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export reports.';
      });
  }
});

export default transactionReportsSlice.reducer;
